import type { Language, LanguageSource } from '@hero/schema';
import {
  isLanguageNeutralInbound,
  stripAllowlistedEmoji,
} from './inbound-reply-tokens';
import {
  classifyLanguageEvidence,
  decideLanguageChange,
  hasNoThreadLanguage,
} from './language-evidence';
import { looksLikeRomanUrdu } from './roman-urdu';
import { distinctiveCharsRegex } from './packs/registry';
import { urPack } from './packs/ur';

/** Urdu-specific code points (the Urdu pack's) — must run before generic Arabic-script test. */
const URDU_CHARS_RE = distinctiveCharsRegex(urPack) as RegExp;
const ARABIC_SCRIPT_RE = /[\u0600-\u06FF]/;
const LATIN_LETTERS_RE = /[A-Za-z]/;

/**
 * Links are Latin no matter who typed them.
 *
 * "الرياض عرعر <maps.app.goo.gl/…>" carries ~16 Arabic letters and ~40
 * Latin ones, so counting raw characters called an Arabic order English
 * — which then reverse-geocoded the shared pin into "Ar Ar / Alaziziyah"
 * and built the whole turn's prompt in the wrong language. A URL says
 * nothing about the language of the person sending it.
 */
const URL_RE = /(?:https?:\/\/|www\.)\S+|\b[\w-]+(?:\.[\w-]+)+\/\S*/gi;

const countScriptLetters = (
  text: string,
): { arabic: number; latin: number } => {
  let arabic = 0;
  let latin = 0;
  for (const ch of text) {
    if (URDU_CHARS_RE.test(ch) || ARABIC_SCRIPT_RE.test(ch)) arabic += 1;
    else if (LATIN_LETTERS_RE.test(ch)) latin += 1;
  }
  return { arabic, latin };
};

/**
 * Infer language from substantive user text.
 * Returns `null` when empty, language-neutral (yes/no/👍), or no script signal (tie).
 */
export const detectAgentLanguageFromText = (text: string): Language | null => {
  const trimmed = text.trim();
  if (!trimmed) return null;
  // Urdu letters settle it even on a bare «ٹھیک ہے» — no Arabic or
  // English speaker types them.
  if (URDU_CHARS_RE.test(stripAllowlistedEmoji(trimmed).replace(URL_RE, ' '))) return 'ur';
  if (isLanguageNeutralInbound(trimmed)) return null;

  const substantive = stripAllowlistedEmoji(trimmed)
    .replace(URL_RE, ' ')
    .trim();
  if (!substantive) return null;

  if (URDU_CHARS_RE.test(substantive)) {
    return 'ur';
  }
  if (ARABIC_SCRIPT_RE.test(substantive) && !LATIN_LETTERS_RE.test(substantive)) {
    return 'ar';
  }
  // Latin-only: Roman Urdu ("mujhe truck chahiye") before English, because
  // drivers overwhelmingly type Urdu in Latin script and were being answered
  // in English on every turn.
  if (LATIN_LETTERS_RE.test(substantive) && !ARABIC_SCRIPT_RE.test(substantive)) {
    return looksLikeRomanUrdu(substantive) ? 'ur' : 'en';
  }

  const { arabic, latin } = countScriptLetters(substantive);
  if (arabic === 0 && latin === 0) return null;
  if (arabic > latin) {
    return URDU_CHARS_RE.test(substantive) ? 'ur' : 'ar';
  }
  if (latin > arabic) {
    return looksLikeRomanUrdu(substantive) ? 'ur' : 'en';
  }
  return null;
};

/**
 * What script the person wrote in, for a thread that has no language
 * yet. The detector calls a bare lane («رياض جدة»), a truck body or a
 * yes/no *neutral* so those tokens never FLIP an established thread —
 * right for turn six, wrong for turn one: a first message in Arabic
 * script is still a person writing Arabic, and answering it with the
 * `en` fallback produced a mixed English/Arabic greeting (runtime, 25
 * Sep 2026). Urdu-only letters → `ur`; Arabic script with no Latin →
 * `ar`; Latin or mixed → `null` (Roman Urdu vs English is undecidable
 * on a city name, so the chain falls through).
 */
export const scriptLanguageHint = (text: string): Language | null => {
  const substantive = stripAllowlistedEmoji(text.trim()).replace(URL_RE, ' ').trim();
  if (!substantive) return null;
  if (URDU_CHARS_RE.test(substantive)) return 'ur';
  if (ARABIC_SCRIPT_RE.test(substantive) && !LATIN_LETTERS_RE.test(substantive)) return 'ar';
  return null;
};

export interface ResolveAgentLanguageInput {
  userText?: string;
  /** What set {@link conversationLanguage} — gates whether this message may replace it. */
  conversationLanguageSource?: LanguageSource;
  /** Running weak-evidence streak on the thread (`Conversation.languageHint`). */
  conversationLanguageHint?: { language: Language; count: number };
  /**
   * Language reported by an engine that heard the audio, already
   * confidence-gated by `resolveDetectedVoiceLanguage`. Outranks
   * {@link userText} because the script heuristic cannot separate `ur`
   * from `ar` on a short transcript — Urdu is written in Arabic
   * script. Callers must not pass a low-confidence detection.
   */
  detectedLanguage?: Language;
  conversationLanguage?: Language;
  headerLanguage?: Language;
  preferredLanguage?: Language;
  fallback?: Language;
}

/**
 * Single priority chain for agent reply language — all channels (in_app, WhatsApp, …).
 * No side effects; pair with `detectAgentLanguageFromText` + DB persist on inbound.
 */
export const resolveAgentLanguage = (input: ResolveAgentLanguageInput): Language => {
  let evidence = classifyLanguageEvidence(input.userText ?? '', {
    detectedVoiceLanguage: input.detectedLanguage,
  });
  if (
    !evidence.language &&
    input.userText &&
    hasNoThreadLanguage({
      language: input.conversationLanguage,
      source: input.conversationLanguageSource,
    })
  ) {
    const hint = scriptLanguageHint(input.userText);
    if (hint) evidence = { language: hint, strength: 'hint' };
  }

  if (input.conversationLanguage) {
    // The same gate `appendInbound` persists through, so the reply
    // language and the stored one can never disagree.
    return (
      decideLanguageChange({
        current: {
          language: input.conversationLanguage,
          source: input.conversationLanguageSource,
          hint: input.conversationLanguageHint,
        },
        evidence,
      }).language ?? input.conversationLanguage
    );
  }

  if (evidence.language) return evidence.language;

  // No thread language yet: the script of what they typed beats every
  // default, neutral token or not.
  if (input.userText) {
    const hint = scriptLanguageHint(input.userText);
    if (hint) return hint;
  }

  return (
    input.headerLanguage ??
    input.preferredLanguage ??
    input.fallback ??
    'en'
  );
};
